import React from 'react';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

export const HeroSection = ({ onOpenContactModal }) => {
  const sectionRef = useScrollReveal();

  return (
    <section 
      id="home"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative overflow-hidden"
      style={{ backgroundColor: '#FFFFFF' }}
    >
      {/* Subtle Abstract Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Large subtle circle - top right */}
        <div 
          className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-[0.03]"
          style={{ backgroundColor: '#0A3D91' }}
        />
        {/* Medium circle - bottom left */}
        <div 
          className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full opacity-[0.02]"
          style={{ backgroundColor: '#00BFFF' }}
        />
        {/* Grid pattern overlay */}
        <div 
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `
              linear-gradient(to right, #0A3D91 1px, transparent 1px),
              linear-gradient(to bottom, #0A3D91 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}
        />
        {/* Subtle gradient line accent */}
        <div 
          className="absolute top-1/2 left-0 w-full h-px opacity-[0.05]"
          style={{ 
            background: 'linear-gradient(90deg, transparent, #0A3D91 20%, #00BFFF 50%, #0A3D91 80%, transparent)' 
          }}
        />
      </div>

      {/* Hero Content */}
      <div className="kv-container relative z-10">
        <div className="pt-10 pb-12 lg:pt-12 lg:pb-14">
          <div className="max-w-4xl mx-auto lg:mx-0 lg:max-w-3xl">
            {/* Eyebrow */}
            <div 
              className="kv-reveal inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-4"
              style={{ 
                borderColor: '#E2E8F0',
                backgroundColor: 'rgba(10, 61, 145, 0.03)'
              }}
              data-testid="hero-eyebrow"
            >
              <span 
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ backgroundColor: '#00BFFF' }}
              />
              <span 
                className="text-sm font-medium tracking-wide"
                style={{ color: '#0A3D91' }}
              >
                Strategic Technology Partner for High-Growth Companies
              </span>
            </div>

            {/* Headline */}
            <h1 
              className="kv-reveal text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] mb-4"
              style={{ color: '#131B2E', letterSpacing: '-0.025em' }}
              data-testid="hero-headline"
            >
              We Build Systems That{' '}
              <span className="relative">
                <span 
                  className="relative z-10"
                  style={{ color: '#0A3D91' }}
                >
                  Scale Revenue, Cut Costs, and Accelerate Growth.
                </span>
                {/* Subtle underline accent */}
                <span 
                  className="absolute bottom-2 left-0 w-full h-3 -z-0 opacity-10"
                  style={{ backgroundColor: '#00BFFF' }}
                />
              </span>
            </h1>

            {/* Subheadline */}
            <p 
              className="kv-reveal text-lg sm:text-xl max-w-2xl mb-6"
              style={{ color: '#475569', lineHeight: '1.7' }}
              data-testid="hero-subheadline"
            >
              KritvaSoft partners with ambitious companies to engineer SaaS platforms, 
              deploy AI-powered automation, and execute growth strategies that deliver 
              measurable ROI — with architecture built for scale from day one.
            </p>

            {/* CTAs */}
            <div 
              className="kv-reveal flex flex-col sm:flex-row items-start sm:items-center gap-4"
              data-testid="hero-ctas"
            >
              <button
                onClick={onOpenContactModal}
                data-testid="hero-cta-primary"
                className="kv-btn-primary px-7 py-3 text-base"
              >
                Book a Strategy Call
                <ArrowRight className="w-5 h-5" />
              </button>
              
              <a
                href="#solutions"
                data-testid="hero-cta-secondary"
                className="kv-btn-outline px-7 py-3 text-base"
              >
                See How We Deliver Results
                <ChevronRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
